import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Download, Upload, FileText, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import { API_BASE_URL } from '../config/apiConfig';

interface ExportConfiguration {
  include_members: boolean;
  include_activity_records: boolean;
  include_activity_categories: boolean;
  include_membership_cycles: boolean;
  include_users: boolean;
  include_audit_logs: boolean;
  date_range_start?: string;
  date_range_end?: string;
  member_ids?: number[];
}

interface ImportConfiguration {
  validate_only: boolean;
  update_existing: boolean;
  skip_conflicts: boolean;
  preserve_ids: boolean;
}

interface ImportResult {
  success: boolean;
  records_processed: number;
  records_imported: number;
  records_updated: number;
  records_skipped: number;
  errors: string[];
  warnings: string[];
}

export const DataManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'export' | 'import' | 'templates'>('export');
  const [exportConfig, setExportConfig] = useState<ExportConfiguration>({
    include_members: true,
    include_activity_records: true,
    include_activity_categories: true,
    include_membership_cycles: true,
    include_users: false,
    include_audit_logs: false,
  });
  
  const [importConfig, setImportConfig] = useState<ImportConfiguration>({
    validate_only: false,
    update_existing: true,
    skip_conflicts: false,
    preserve_ids: false,
  });
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Get export templates
  const { data: templates } = useQuery({
    queryKey: ['export-templates'],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api/data/export-templates`);
      if (!response.ok) throw new Error('Failed to fetch templates');
      return response.json();
    }
  });

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: async (config: ExportConfiguration) => {
      const response = await fetch(`${API_BASE_URL}/api/data/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(config),
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `san_data_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onSuccess: () => {
      alert('Data export completed successfully!');
    },
    onError: (error) => {
      alert(`Export failed: ${error.message}`);
    }
  });

  // Import mutation
  const importMutation = useMutation({
    mutationFn: async ({ file, config }: { file: File; config: ImportConfiguration }) => {
      const formData = new FormData();
      formData.append('file', file);
      
      // Add config as query params
      const params = new URLSearchParams();
      Object.entries(config).forEach(([key, value]) => {
        params.append(key, value.toString());
      });
      
      const response = await fetch(`${API_BASE_URL}/api/data/import?${params}`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) throw new Error('Import failed');
      return response.json();
    },
    onSuccess: (result: ImportResult) => {
      setImportResult(result);
    },
    onError: (error) => {
      setImportResult({
        success: false,
        records_processed: 0,
        records_imported: 0,
        records_updated: 0,
        records_skipped: 0,
        errors: [error.message],
        warnings: []
      });
    }
  });

  const handleExport = () => {
    exportMutation.mutate(exportConfig);
  };

  const handleImport = () => {
    if (!selectedFile) {
      alert('Please select a file to import');
      return;
    }
    importMutation.mutate({ file: selectedFile, config: importConfig });
  };

  const downloadTemplate = (templateName: string, template: any) => {
    const sampleData = {
      export_metadata: {
        timestamp: new Date().toISOString(),
        exported_by: "Template Generator",
        export_config: {},
        system_version: "1.0"
      },
      data: {
        [templateName]: [template]
      }
    };
    
    const blob = new Blob([JSON.stringify(sampleData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${templateName}_template.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Data Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('export')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'export' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <Download className="w-4 h-4 inline mr-2" />
            Export
          </button>
          <button
            onClick={() => setActiveTab('import')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'import' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <Upload className="w-4 h-4 inline mr-2" />
            Import
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 rounded-md ${
              activeTab === 'templates' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Templates
          </button>
        </div>
      </div>

      {/* Export Tab */}
      {activeTab === 'export' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Export Data</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3">Data to Include</h3>
              <div className="space-y-3">
                {[
                  { key: 'include_members', label: 'Members', description: 'Organization member information' },
                  { key: 'include_activity_records', label: 'Activity Records', description: 'All activity submissions and token awards' },
                  { key: 'include_activity_categories', label: 'Activity Categories', description: 'Category definitions and token values' },
                  { key: 'include_membership_cycles', label: 'Membership Cycles', description: 'Member progress and cycle status' },
                  { key: 'include_users', label: 'Users (Sensitive)', description: 'System user accounts and roles' },
                  { key: 'include_audit_logs', label: 'Audit Logs (Large)', description: 'System change history' },
                ].map(({ key, label, description }) => (
                  <label key={key} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={exportConfig[key as keyof ExportConfiguration] as boolean}
                      onChange={(e) => setExportConfig(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-sm text-gray-500">{description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-3">Date Range (Optional)</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={exportConfig.date_range_start || ''}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, date_range_start: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={exportConfig.date_range_end || ''}
                    onChange={(e) => setExportConfig(prev => ({ ...prev, date_range_end: e.target.value }))}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={handleExport}
                  disabled={exportMutation.isPending}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center justify-center"
                >
                  {exportMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4 mr-2" />
                      Export Data
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex">
              <Info className="w-5 h-5 text-blue-400 mr-2" />
              <div className="text-sm text-blue-800">
                <strong>Export Notes:</strong>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Exported data will be in JSON format for easy re-import</li>
                  <li>Use date range filters to limit the size of activity records and audit logs</li>
                  <li>Users and audit logs contain sensitive data - export carefully</li>
                  <li>Always test imports on a backup system first</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Tab */}
      {activeTab === 'import' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Import Data</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium mb-3">File Selection</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">JSON Data File</label>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                  {selectedFile && (
                    <p className="mt-2 text-sm text-gray-600">Selected: {selectedFile.name}</p>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-medium mb-3 mt-6">Import Options</h3>
              <div className="space-y-3">
                {[
                  { key: 'validate_only', label: 'Validate Only', description: 'Test import without making changes' },
                  { key: 'update_existing', label: 'Update Existing', description: 'Update records that already exist' },
                  { key: 'skip_conflicts', label: 'Skip Conflicts', description: 'Skip records that would cause conflicts' },
                  { key: 'preserve_ids', label: 'Preserve IDs', description: 'Attempt to preserve original record IDs' },
                ].map(({ key, label, description }) => (
                  <label key={key} className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={importConfig[key as keyof ImportConfiguration]}
                      onChange={(e) => setImportConfig(prev => ({ ...prev, [key]: e.target.checked }))}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">{label}</div>
                      <div className="text-sm text-gray-500">{description}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="mt-6">
                <button
                  onClick={handleImport}
                  disabled={!selectedFile || importMutation.isPending}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
                >
                  {importMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {importConfig.validate_only ? 'Validating...' : 'Importing...'}
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      {importConfig.validate_only ? 'Validate Data' : 'Import Data'}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Import Results */}
            <div>
              {importResult && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Import Results</h3>
                  
                  <div className={`p-4 rounded-md ${
                    importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {importResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-400 mr-2" />
                      )}
                      <span className={`font-medium ${
                        importResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {importResult.success ? 'Import Successful' : 'Import Failed'}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="text-sm font-medium text-gray-600">Processed</div>
                      <div className="text-lg font-semibold text-gray-900">{importResult.records_processed}</div>
                    </div>
                    <div className="bg-green-50 p-3 rounded-md">
                      <div className="text-sm font-medium text-green-600">Imported</div>
                      <div className="text-lg font-semibold text-green-900">{importResult.records_imported}</div>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-md">
                      <div className="text-sm font-medium text-blue-600">Updated</div>
                      <div className="text-lg font-semibold text-blue-900">{importResult.records_updated}</div>
                    </div>
                    <div className="bg-yellow-50 p-3 rounded-md">
                      <div className="text-sm font-medium text-yellow-600">Skipped</div>
                      <div className="text-lg font-semibold text-yellow-900">{importResult.records_skipped}</div>
                    </div>
                  </div>

                  {importResult.errors.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-4">
                      <h4 className="font-medium text-red-800 mb-2">Errors</h4>
                      <ul className="text-sm text-red-700 space-y-1">
                        {importResult.errors.map((error, index) => (
                          <li key={index}>• {error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importResult.warnings.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                      <h4 className="font-medium text-yellow-800 mb-2">Warnings</h4>
                      <ul className="text-sm text-yellow-700 space-y-1">
                        {importResult.warnings.map((warning, index) => (
                          <li key={index}>• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <AlertTriangle className="w-5 h-5 text-yellow-400 mr-2" />
              <div className="text-sm text-yellow-800">
                <strong>Import Warning:</strong>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  <li>Always backup your data before importing</li>
                  <li>Use "Validate Only" mode first to check for errors</li>
                  <li>Import operations cannot be undone easily</li>
                  <li>Large imports may take several minutes to complete</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Data Templates & Samples</h2>
          
          {templates && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(templates.templates).map(([templateName, template]) => (
                  <div key={templateName} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="text-lg font-medium text-gray-900">
                        {templateName.replace('_template', '').replace('_', ' ').toUpperCase()}
                      </h3>
                      <button
                        onClick={() => downloadTemplate(templateName.replace('_template', ''), template)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        <Download className="w-4 h-4 inline mr-1" />
                        Download
                      </button>
                    </div>
                    <pre className="text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto max-h-40">
                      {JSON.stringify(template, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <h3 className="font-medium text-blue-800 mb-2">Usage Instructions</h3>
                <div className="text-sm text-blue-700 space-y-2">
                  {templates.notes && Object.entries(templates.notes).map(([key, note]) => (
                    <div key={key}>
                      <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {note as string}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};